import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "react-query";

import axios from "axios";
import { useState, useMemo, useRef, useCallback, useEffect } from "react";
import { nuclearn_api_url } from "src/App";
import {
  useNotificationStream,
  isNotificationStreamHealthy,
  useSSEFallbackInvalidation,
} from "src/reusable/hooks/useNotificationStream";
import { CHUNKED_UPLOAD_THRESHOLD } from "src/constants";
import { whereFilterListToJsonArray } from "src/helpers/helperService";
import { NucToast } from "src/reusable/components/Toast";
import { getFormattedDate } from "src/reusable/Functions";
import { uploadFileInChunks } from "src/reusable/services/unifiedChunkedUploadService";

// Dataset types whose "download all" returns a zip archive (not a single
// CSV file). Keep this list in sync with the backend's archive-capable
// types — both the async stream endpoint and the sync /download-all route
// check against it.
export const DOCUMENT_LIKE_DATASET_TYPES = [
  "document",
  "Activity",
  "ProjectAI",
];

// Optimized cache times for stale-while-revalidate pattern
const CACHE_CONFIG = {
  // Static/rarely changing data - longer stale time
  STATIC: {
    staleTime: 10 * 60 * 1000, // 10 minutes
    cacheTime: 30 * 60 * 1000, // 30 minutes
  },
  // Frequently updated data - shorter stale time, background refetch
  DYNAMIC: {
    staleTime: 30 * 1000, // 30 seconds
    cacheTime: 5 * 60 * 1000, // 5 minutes
    refetchOnWindowFocus: true,
    refetchInterval: 60 * 1000, // 1 minute background refetch
  },
  // User-specific data - medium stale time
  USER_SPECIFIC: {
    staleTime: 2 * 60 * 1000, // 2 minutes
    cacheTime: 10 * 60 * 1000, // 10 minutes
    refetchOnWindowFocus: true,
  },
  // Search results - very short stale time
  SEARCH: {
    staleTime: 10 * 1000, // 10 seconds
    cacheTime: 2 * 60 * 1000, // 2 minutes
    refetchOnWindowFocus: false,
  },
};

const DOCUMENT_DATASET_TYPE = 2;

const getDataset = (id) => {
  return axios.get(`${nuclearn_api_url}/datasets/${id}/`).then((response) => {
    return response.data;
  });
};

export const useGetDataset = (id) => {
  // Canonicalize the id to a NUMBER for the cache key. Route components pass
  // the id as a string (useParams) while every invalidation site (share
  // mutations, UpdateDatasetModal, upload completion) uses a numeric id —
  // react-query compares keys by deep equality, so a string-keyed entry is
  // never matched by those invalidations (the admin self-grant interstitial
  // sat on a stale 403 exactly this way), and the same dataset gets cached
  // twice under two keys.
  const numericId = id == null || id === "" ? undefined : Number(id);
  // No retry override: the global default (retryUnlessClientError) fails
  // fast on every deterministic 4xx — 403 for unshared datasets, 404 for
  // missing ones — and keeps the 3-attempt budget for transient errors.
  return useQuery(["dataset", numericId], () => getDataset(numericId), {
    ...CACHE_CONFIG.USER_SPECIFIC,
    enabled: Boolean(numericId),
  });
};

export const getMultipleDatasets = (datasetIds) => {
  if (!datasetIds || datasetIds.length === 0) {
    return Promise.resolve([]);
  }

  const idsParam = datasetIds.join(",");
  return axios
    .get(`${nuclearn_api_url}/datasets/ids`, {
      params: { ids: idsParam },
    })
    .then((response) => {
      return response.data;
    })
    .catch((error) => {
      console.error("Error fetching multiple datasets:", error);
      return [];
    });
};

export const useGetMultipleDatasets = (datasetIds) => {
  // Create a cache key from sorted IDs to ensure consistent queries
  const cacheKey =
    datasetIds && datasetIds.length > 0
      ? [...datasetIds].sort((a, b) => a - b).join(",")
      : "";

  return useQuery(
    ["multipleDatasets", cacheKey],
    () => getMultipleDatasets(datasetIds),
    {
      ...CACHE_CONFIG.USER_SPECIFIC,
      enabled: Boolean(datasetIds && datasetIds.length > 0),
    },
  );
};

const getDatasetByName = (name) => {
  return axios
    .get(`${nuclearn_api_url}/datasets/`, {
      params: { dataset_name: name },
    })
    .then((response) => {
      return response.data;
    });
};

export const useGetDatasetByName = (name) => {
  return useQuery(["dataset", name], () => getDatasetByName(name), {
    ...CACHE_CONFIG.USER_SPECIFIC,
    enabled: Boolean(name),
  });
};

// TODO: implement logic to fetch if there is a scored dataset for both functions
const getScoredDataset = (datasetId, modelVersionId) => {
  return axios
    .get(
      `${nuclearn_api_url}/datasets/${datasetId}/can-analyze-dataset/${modelVersionId}`,
    )
    .then((response) => {
      return response.data;
    });
};

export const useGetScoredDataset = (datasetId, modelVersionId) => {
  return useQuery(
    ["isScored", datasetId, modelVersionId],
    () => getScoredDataset(datasetId, modelVersionId),
    {
      refetchOnWindowFocus: false,
      enabled: (Boolean(datasetId), Boolean(modelVersionId)),
    },
  );
};

const getDatasetIsReadyForSearch = (datasetId) => {
  return axios
    .get(
      `${nuclearn_api_url}/search/search-ready-count?dataset_id=${datasetId}`,
    )
    .then((response) => {
      return response.data;
    });
};

export const useGetDatasetIsReadyForSearch = ({ datasetId }) => {
  const queryClient = useQueryClient();
  const queryKey = ["isDatasetReadyForSearch", datasetId];

  // Subscribe to per-SDR terminal broadcasts for this dataset. The backend
  // fires notify:datasets:changed with action=sdr_status_changed whenever an
  // SDR in a dataset hits a terminal state — exactly what drives the
  // "num_sdr_not_embedded" count. Invalidate on matching events so the count
  // updates in real time without polling.
  useNotificationStream(
    datasetId ? "notify:datasets:changed" : null,
    "dataset_changed",
    (data) => {
      if (
        data?.action === "sdr_status_changed" &&
        data?.dataset_id === datasetId
      ) {
        queryClient.invalidateQueries(queryKey);
      }
    },
  );

  useSSEFallbackInvalidation(queryClient, queryKey, Boolean(datasetId));

  return useQuery(queryKey, () => getDatasetIsReadyForSearch(datasetId), {
    staleTime: 30 * 1000, // 30 seconds — embedding status changes slowly
    cacheTime: 5 * 60 * 1000,
    // SSE handles updates when healthy; fall back to 30s poll only when down.
    refetchInterval: () => (isNotificationStreamHealthy() ? false : 30 * 1000),
    refetchOnWindowFocus: false,
    enabled: Boolean(datasetId),
  });
};

// New processing status endpoint that provides granular status tracking
const getProcessingStatus = ({ sdrIds, datasetIds }) => {
  if (
    (!sdrIds || sdrIds.length === 0) &&
    (!datasetIds || datasetIds.length === 0)
  ) {
    return Promise.resolve([]);
  }

  const params = new URLSearchParams();
  if (sdrIds && sdrIds.length > 0) {
    const sdrIdsParam = Array.isArray(sdrIds)
      ? sdrIds.join(",")
      : String(sdrIds);
    params.append("sdr_ids", sdrIdsParam);
  }
  if (datasetIds && datasetIds.length > 0) {
    const datasetIdsParam = Array.isArray(datasetIds)
      ? datasetIds.join(",")
      : String(datasetIds);
    params.append("dataset_ids", datasetIdsParam);
  }

  // Errors propagate so callers can tell a failed request from an empty result.
  return axios
    .get(`${nuclearn_api_url}/datasets/processing-status?${params.toString()}`)
    .then((response) => response.data);
};

export const useGetProcessingStatus = ({
  sdrIds,
  datasetIds,
  shouldPoll = false,
  pollInterval = 3000,
  enabled = true,
}) => {
  const queryClient = useQueryClient();

  // Create cache key from both sdrIds and datasetIds. Copy before sort so we
  // don't mutate caller-owned arrays (props/state).
  const sdrIdsKey = Array.isArray(sdrIds)
    ? [...sdrIds].sort().join(",")
    : String(sdrIds || "");
  const datasetIdsKey = Array.isArray(datasetIds)
    ? [...datasetIds].sort().join(",")
    : String(datasetIds || "");
  const cacheKey = `${sdrIdsKey}|${datasetIdsKey}`;

  // Debounced invalidation — collapses rapid SSE events (e.g., tabular dataset
  // indexing where thousands of SDRs complete per second) into a single refetch.
  const debounceTimer = useRef(null);
  const debouncedInvalidate = useCallback(() => {
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => {
      queryClient.invalidateQueries(["processingStatus", cacheKey]);
      // Also refresh facet counts so the "Processing Status" filter sidebar
      // reflects the new status distribution after each SSE batch.
      (datasetIds ?? []).forEach((id) => {
        queryClient.invalidateQueries(["facets", id]);
      });
    }, 2000);
  }, [queryClient, cacheKey, datasetIdsKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // Clear the pending debounce on unmount or cacheKey change — otherwise a
  // late timer fires into a dead query client (or invalidates a query key
  // the caller no longer cares about, re-waking a detached useQuery).
  useEffect(() => {
    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
        debounceTimer.current = null;
      }
    };
  }, [cacheKey]);

  // Subscribe to SSE for each SDR — invalidate the batch query on any event
  const sseStreamKey =
    sdrIds && sdrIds.length === 1 ? `notify:sdr:${sdrIds[0]}:status` : null;

  useNotificationStream(sseStreamKey, "sdr_status", debouncedInvalidate);

  // For multiple SDRs, subscribe to the dataset-level stream instead.
  // Filter to SDR-status broadcasts for the datasets we care about — a
  // wildcard would also wake us up on unrelated dataset CRUD events.
  const datasetStreamKey =
    !sseStreamKey && datasetIds && datasetIds.length > 0
      ? "notify:datasets:changed"
      : null;

  const watchedDatasetIdSet = useMemo(
    () => new Set((datasetIds ?? []).map(Number)),
    [datasetIdsKey], // eslint-disable-line react-hooks/exhaustive-deps
  );

  useNotificationStream(datasetStreamKey, "dataset_changed", (data) => {
    if (
      data?.action === "sdr_status_changed" &&
      watchedDatasetIdSet.has(Number(data?.dataset_id))
    ) {
      debouncedInvalidate();
    }
  });

  useSSEFallbackInvalidation(
    queryClient,
    ["processingStatus", cacheKey],
    enabled && !!sseStreamKey,
  );

  return useQuery(
    ["processingStatus", cacheKey],
    () => getProcessingStatus({ sdrIds, datasetIds }),
    {
      refetchInterval: () => {
        // If SSE is healthy and we have a single-SDR subscription, skip polling
        if (sseStreamKey && isNotificationStreamHealthy()) {
          return false;
        }
        // Fallback to polling when requested
        return shouldPoll ? pollInterval : false;
      },
      refetchOnWindowFocus: false,
      enabled:
        enabled &&
        Boolean(
          (sdrIds && (Array.isArray(sdrIds) ? sdrIds.length > 0 : true)) ||
          (datasetIds &&
            (Array.isArray(datasetIds) ? datasetIds.length > 0 : true)),
        ),
      // Keep previous data while refetching to prevent UI flicker
      keepPreviousData: true,
    },
  );
};

const getDatasetSearchResults = (
  numRecords,
  datasetId,
  searchText,
  whereFilterList,
) => {
  const filt = whereFilterListToJsonArray(whereFilterList);
  const payload = {
    search_query: searchText,
  };
  const params = new URLSearchParams();
  params.append("n_results", numRecords);
  params.append("with_sources", "true");
  params.append("dataset_id", datasetId);
  if (filt) {
    params.append("filt", filt);
  }
  return axios
    .post(`${nuclearn_api_url}/search/?${params.toString()}`, payload)
    .then((response) => {
      return response.data;
    });
};

// eslint-disable-next-line no-unused-vars
const useGetSearchResults = (
  numRecords,
  datasetId,
  searchText,
  whereFilters,
) => {
  return useQuery(
    ["dataset search result", datasetId, searchText],
    () =>
      getDatasetSearchResults(numRecords, datasetId, searchText, whereFilters),
    {
      keepPreviousData: true,
      enabled: false, // leave this disabled and use the manual refetch function to get the results by clicking the search button,
    },
  );
};

/**
 * Search within a single document (intra-document search).
 * Sends the record alone: search unions every scope it is given, so adding the
 * record's dataset would search the whole dataset.
 */
export const searchWithinDocument = (
  sourceDataRecordId,
  searchText,
  numResults = 10,
) => {
  const payload = {
    search_query: searchText,
  };
  return axios
    .post(
      `${nuclearn_api_url}/search/?n_results=${numResults}&with_sources=true&source_data_record_ids=${sourceDataRecordId}`,
      payload,
    )
    .then((response) => {
      return response.data;
    });
};

/**
 * Hook for intra-document search
 */
export const useSearchWithinDocument = (
  sourceDataRecordId,
  searchText,
  numResults = 10,
) => {
  return useQuery(
    ["intra-document-search", sourceDataRecordId, searchText],
    () => searchWithinDocument(sourceDataRecordId, searchText, numResults),
    {
      enabled: Boolean(
        sourceDataRecordId && searchText && searchText.length > 0,
      ),
      keepPreviousData: true,
      staleTime: 30 * 1000, // 30 seconds
    },
  );
};

const getDatasetChunkingStrategy = (datasetId) => {
  let url = datasetId
    ? `/search/chunking-strategy?dataset_id=${datasetId}`
    : `/search/chunking-strategy`;
  return axios.get(nuclearn_api_url + url).then((response) => {
    return response.data;
  });
};

export const useGetDatasetChunkingStrategy = (datasetId) => {
  return useQuery(
    ["datasetChunkingStrategies", datasetId],
    () => getDatasetChunkingStrategy(datasetId),
    {
      ...CACHE_CONFIG.STATIC, // Chunking strategies rarely change
    },
  );
};

const updateDatasetChunkingStrategy = (datasetId, chunkStrategyId) => {
  let payload = {
    dataset_id: datasetId,
    chunk_strategy_id: chunkStrategyId,
  };
  return axios
    .patch(`${nuclearn_api_url}/search/dataset-chunk-strategy`, payload)
    .then((response) => {
      return response.data;
    });
};

export const useUpdateDatasetChunkingStrategy = () => {
  const queryClient = useQueryClient();

  return useMutation(
    ([datasetId, chunkStrategyId]) =>
      updateDatasetChunkingStrategy(datasetId, chunkStrategyId),
    {
      onSuccess: (data, vars) => {
        const [datasetId] = vars;
        queryClient.invalidateQueries(["datasetChunkingStrategies", datasetId]);
      },
      onError: (error) => {
        console.log(error.response);
      },
    },
  );
};

const getDatasetList = (
  onlyLogicallyDeleted = false,
  dataset_type_id = null,
  publicOnly = false,
) => {
  const params = new URLSearchParams();

  if (onlyLogicallyDeleted) {
    params.set("only_logically_deleted", "true");
  }
  if (dataset_type_id) {
    params.set("dataset_type_id", dataset_type_id);
  }
  if (publicOnly) {
    params.set("public_only", publicOnly);
  }

  const query = params.toString();
  const url = query
    ? `${nuclearn_api_url}/datasets/?${query}`
    : `${nuclearn_api_url}/datasets/`;

  return axios.get(url).then((response) => {
    const data = Array.isArray(response.data) ? response.data : [];
    return data.sort((a, b) => {
      if (a.id < b.id) {
        return -1;
      }
      if (a.id > b.id) {
        return 1;
      }
      return 0;
    });
  });
};

export const useDatasetList = (
  onlyLogicallyDeleted = false,
  dataset_type_id = null,
  publicOnly = false,
) => {
  const queryClient = useQueryClient();
  const queryKey = [
    "datasetList",
    onlyLogicallyDeleted,
    dataset_type_id,
    publicOnly,
  ];

  // notify:datasets:changed is multi-purpose — it also carries
  // SDR-status-derived broadcasts (action="sdr_status_changed") used by
  // useGetProcessingStatus. Only invalidate the collection list when the
  // collection itself actually changed.
  useNotificationStream(
    "notify:datasets:changed",
    "dataset_changed",
    (data) => {
      const action = data?.action;
      if (
        action === "created" ||
        action === "deleted" ||
        action === "updated"
      ) {
        queryClient.invalidateQueries(["datasetList"]);
      }
    },
  );

  useSSEFallbackInvalidation(queryClient, queryKey);

  return useQuery(
    queryKey,
    () => getDatasetList(onlyLogicallyDeleted, dataset_type_id, publicOnly),
    {
      ...CACHE_CONFIG.DYNAMIC,
      // SSE handles dataset change notifications when healthy; fall back to
      // a slow poll if SSE is unreachable so the list still refreshes.
      refetchInterval: () => (isNotificationStreamHealthy() ? false : 30_000),
      refetchIntervalInBackground: false,
      refetchOnMount: true,
      retry: 3,
    },
  );
};

const getDatasetFields = (dataset_id) => {
  return axios
    .get(`${nuclearn_api_url}/datasets/${dataset_id}/fields`)
    .then((response) => response.data);
};

export const useDatasetFields = (dataset_id) => {
  return useQuery(
    ["datasetFields", dataset_id],
    () => getDatasetFields(dataset_id),
    {
      ...CACHE_CONFIG.STATIC, // Fields don't change often
      enabled: Boolean(dataset_id),
      retry: 2,
    },
  );
};

// Update dataset fields (e.g., filter_type_override, visibility, order)
const updateDatasetFields = (datasetId, fields) => {
  return axios
    .patch(`${nuclearn_api_url}/datasets/${datasetId}/fields`, fields)
    .then((response) => response.data);
};

export const useUpdateDatasetFields = () => {
  const queryClient = useQueryClient();
  return useMutation(
    ({ datasetId, fields }) => updateDatasetFields(datasetId, fields),
    {
      onSuccess: (_, { datasetId }) => {
        // Invalidate fields and filter schema caches
        queryClient.invalidateQueries(["datasetFields", datasetId]);
        queryClient.invalidateQueries(["filterSchema", datasetId]);
      },
    },
  );
};

const getDatasetModelsByID = (dataset_id) => {
  return axios
    .get(`${nuclearn_api_url}/datasets/${dataset_id}/models`)
    .then((response) => {
      return response.data;
    });
};

export const useGetDatasetModelsByID = (dataset_id) => {
  return useQuery(
    ["datasetModels", dataset_id],
    () => getDatasetModelsByID(dataset_id),
    {
      refetchOnWindowFocus: false,
      enabled: dataset_id !== "",
    },
  );
};

const createDataset = (dataset) => {
  return axios
    .put(`${nuclearn_api_url}/datasets/create-dataset`, dataset)
    .then((response) => {
      return response.data;
    });
};

export const useCreateDataset = () => {
  const queryClient = useQueryClient();

  return useMutation(createDataset, {
    onSuccess: () => {
      queryClient.invalidateQueries("datasetList");
    },
    onError: (error) => {
      console.log(error.response);
    },
  });
};

const createRemoteDataset = (dataset) => {
  return axios
    .put(`${nuclearn_api_url}/datasets/create-remote-dataset`, dataset)
    .then((response) => {
      return response.data;
    });
};
export const syncRemoteDataset = async (dataset_id) => {
  return await axios
    .post(`${nuclearn_api_url}/datasets/sync-remote-dataset/${dataset_id}`)
    .then((response) => {
      return response.data;
    });
};

// eslint-disable-next-line no-unused-vars
const getSyncStatus = async (dataset_id, task_id) => {
  return await axios
    .get(
      `${nuclearn_api_url}/datasets/sync-remote-dataset/${dataset_id}/status/${task_id}`,
    )
    .then((response) => {
      return response.data;
    });
};

// Hook to poll sync task status
export const useSyncStatus = (datasetId, taskId, options = {}) => {
  return useQuery(
    ["syncStatus", datasetId, taskId],
    async () => {
      if (!datasetId || !taskId) return null;
      const response = await axios.get(
        `${nuclearn_api_url}/datasets/sync-remote-dataset/${datasetId}/status/${taskId}`,
      );
      return response.data;
    },
    {
      enabled: !!datasetId && !!taskId,
      refetchInterval: (data) => {
        // Stop polling if task completed or failed
        if (!data || data.state === "SUCCESS" || data.state === "FAILURE") {
          return false;
        }
        return 1000; // Poll every second
      },
      ...options,
    },
  );
};

export const useSyncRemoteDataset = () => {
  const queryClient = useQueryClient();
  return useMutation(syncRemoteDataset, {
    onSuccess: () => {
      // This invalidation is intended to come after the successful sync of the remote dataset so that we only invalidate once. If we remove this and
      // don't change the invalidation in the createRemoteDataset function to invalidate the datasetlist, we will won't invalidate.
      queryClient.invalidateQueries("datasetList");
    },
    onError: () => {
      // Error handling
    },
  });
};

export const useCreateRemoteDataset = () => {
  const queryClient = useQueryClient();
  return useMutation(createRemoteDataset, {
    onSuccess: () => {
      queryClient.invalidateQueries("datasetList");
    },
    onError: () => {
      queryClient.invalidateQueries("datasetList");
    },
  });
};

const updateDataset = (dataset) => {
  return axios
    .patch(`${nuclearn_api_url}/datasets/update-dataset/${dataset.id}`, dataset)
    .then((response) => {
      return response.data;
    });
};

export const useUpdateDataset = () => {
  return useMutation(updateDataset, {
    onError: (error) => {
      console.log(error.response);
    },
  });
};

const deletePermanentlyDataset = (datasets) => {
  return axios.delete(
    `${nuclearn_api_url}/datasets/permanent-delete-datasets-by-ids`,
    { data: datasets.map((dataset) => dataset.id) },
  );
};

export const useDeletePermanentlyDataset = () => {
  const queryClient = useQueryClient();

  return useMutation(deletePermanentlyDataset, {
    onSuccess: () => {
      queryClient.invalidateQueries("datasetList");
    },
    onError: () => {
      // Error handling
    },
  });
};

const restoreDataset = (datasetId) => {
  return axios
    .post(`${nuclearn_api_url}/datasets/${datasetId}/restore`)
    .then((response) => response.data);
};

export const useRestoreDataset = () => {
  const queryClient = useQueryClient();

  return useMutation(restoreDataset, {
    onSuccess: () => {
      queryClient.invalidateQueries("datasetList");
    },
  });
};

const deleteDataset = (dataset) => {
  return axios
    .delete(`${nuclearn_api_url}/datasets/delete-dataset/${dataset.id}`)
    .then(() => {
      return dataset.id;
    });
};

export const deleteSDRById = (sdr_id) => {
  return axios({
    method: "delete",
    url: `${nuclearn_api_url}/datasets/delete-sdr-by-id/${sdr_id}`,
  }).then((response) => {
    return response.data;
  });
};

export const useDeleteSDRById = () => {
  const queryClient = useQueryClient();

  const mutation = useMutation(deleteSDRById, {
    onSuccess: () => {
      queryClient.invalidateQueries("allSourceDataRecords");
      queryClient.invalidateQueries("allArtifactsByDatasetId");
    },
    onError: (error) => {
      console.error(error.response);
    },
  });
  return mutation;
};

// Reprocess a source data record (re-OCR, re-chunk, re-embed)
const reprocessSDRById = (sdr_id) => {
  return axios({
    method: "post",
    url: `${nuclearn_api_url}/datasets/source-data-record/${sdr_id}/reprocess`,
  }).then((response) => {
    return response.data;
  });
};

export const useReprocessSDRById = () => {
  const queryClient = useQueryClient();

  const mutation = useMutation(reprocessSDRById, {
    onSuccess: () => {
      queryClient.invalidateQueries("allSourceDataRecords");
      queryClient.invalidateQueries("allArtifactsByDatasetId");
      queryClient.invalidateQueries(["processingStatus"]);
      NucToast({
        message: "Document reprocessing started",
        type: "success",
      });
    },
    onError: (error) => {
      const errorMessage =
        error.response?.data?.detail || "Error starting reprocessing";
      NucToast({
        message: errorMessage,
        type: "error",
      });
      console.error("Reprocess error:", error.response);
    },
  });
  return mutation;
};

// Reprocess a source data record with realtime priority (re-runs the full document workflow)
const reprocessSdr = (sdr_id) => {
  return axios({
    method: "post",
    url: `${nuclearn_api_url}/datasets/source-data-record/${sdr_id}/reprocess`,
    params: { queue_tier: "realtime" },
  }).then((response) => {
    return response.data;
  });
};

export const useReprocessSdr = () => {
  const queryClient = useQueryClient();

  return useMutation(reprocessSdr, {
    onSuccess: () => {
      queryClient.invalidateQueries("allSourceDataRecords");
      queryClient.invalidateQueries("allArtifactsByDatasetId");
      queryClient.invalidateQueries(["processingStatus"]);
    },
  });
};

export const useDeleteDataset = () => {
  const queryClient = useQueryClient();

  return useMutation(deleteDataset, {
    onSuccess: () => {
      NucToast({
        message: `Deleted successfully!`,
        type: "success",
      });
      queryClient.invalidateQueries("datasetList");
    },
    onError: (error) => {
      console.log(error.response);
    },
  });
};

const deleteAllDatasetFields = (datasetId) => {
  return axios.delete(`${nuclearn_api_url}/datasets/${datasetId}/fields`);
};

const clearDataset = (datasetId) => {
  return axios
    .patch(`${nuclearn_api_url}/datasets/clear-dataset/${datasetId}`)
    .then((response) => {
      // 202: the clear drains in a background task, which removes the fields
      // itself once the records are gone.
      if (response.status !== 202) {
        deleteAllDatasetFields(datasetId);
      }
      return response;
    });
};

export const useClearDataset = () => {
  const queryClient = useQueryClient();
  return useMutation(clearDataset, {
    onSuccess: () => {
      queryClient.invalidateQueries("allSourceDataRecords");
    },
    onError: () => {
      // Error handling
    },
  });
};

export const putTempPersistTabular = async (
  file,
  onSuccess,
  onError,
  onProgress,
) => {
  try {
    const fileExtension = "." + file.name.split(".").pop()?.toLowerCase();
    const chunkableExtensions = [".csv", ".tsv", ".txt"];
    const isChunkable = chunkableExtensions.includes(fileExtension);

    if (isChunkable) {
      // Use chunked upload for text-based files (CSV, TSV, TXT)
      const response = await uploadFileInChunks({
        context: "temp-tabular",
        contextId: "temp", // Required for temp tabular uploads
        file,
        chunkSize: CHUNKED_UPLOAD_THRESHOLD, // Use the configured chunk size
        onProgress: (progress) => {
          if (onProgress) {
            onProgress(progress);
          }
        },
        onChunkProgress: (chunkIndex, isUploaded, error) => {
          if (error) {
            console.error(`Error uploading chunk ${chunkIndex}: ${error}`);
          } else {
            console.log(`Chunk ${chunkIndex} uploaded successfully`);
          }
        },
      });

      // The response.result should contain: { temp_file_id, columns, n_rows }
      onSuccess({ data: response.result });
    } else {
      // Use original upload for binary files (XLSX, XLS)
      let formData = new FormData();
      formData.append("csv_file", file);

      axios({
        method: "put",
        url: `${nuclearn_api_url}/datasets/temp-persist-tabular`,
        data: formData,
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: onProgress
          ? (progressEvent) => {
              const progress = Math.round(
                (progressEvent.loaded * 100) / progressEvent.total,
              );
              onProgress(progress);
            }
          : undefined,
      })
        .then(onSuccess)
        .catch(onError);
    }
  } catch (error) {
    onError(error);
  }
};

export const putTempPersistFile = async (
  file,
  datasetId,
  fileType,
  onSuccess,
  onError,
  onUploadProgress,
) => {
  try {
    // ALWAYS use chunked upload for all files
    const response = await uploadFileInChunks({
      context: "dataset",
      contextId: String(datasetId),
      file,
      onProgress: (progress) => {
        if (onUploadProgress) {
          // Convert progress to axios-like event
          onUploadProgress({
            loaded: (progress / 100) * file.size,
            total: file.size,
          });
        }
      },
      onChunkProgress: () => {
        // Chunk progress tracking
      },
    });

    onSuccess({ data: response.result });
  } catch (error) {
    onError(error);
  }
};

// eslint-disable-next-line no-unused-vars
const usePutTempPersistFile = () => {
  const queryClient = useQueryClient();
  const mutation = useMutation(putTempPersistFile, {
    onSuccess: () => {
      //TODO: make this more specific to the source data record
      queryClient.invalidateQueries("allSourceDataRecords");
      queryClient.invalidateQueries("allArtifactsByDatasetId");
    },
    onError: (error) => {
      console.log(error.response);
    },
  });
  return mutation;
};

export const syncCsv = (
  dataset_id,
  tempFileId,
  uniqueIdColumnName,
  onSuccess,
  onError,
) => {
  axios
    .post(`${nuclearn_api_url}/datasets/${dataset_id}/sync-csv`, {
      temp_file_id: tempFileId,
      unique_id_column_name: uniqueIdColumnName,
    })
    .then(onSuccess)
    .catch(onError);
};

export const syncCsvDetails = (
  dataset_id,
  tempFileId,
  uniqueIdColumnName,
  detailName,
  onSuccess,
  onError,
) => {
  axios
    .post(`${nuclearn_api_url}/datasets/${dataset_id}/sync-detail-csv`, {
      temp_file_id: tempFileId,
      unique_id_column_name: uniqueIdColumnName,
      detail_name: detailName,
    })
    .then(onSuccess)
    .catch(onError);
};

const getDatasetModelVersions = (datasetId) => {
  if (!datasetId) {
    return;
  }
  return axios
    .get(`${nuclearn_api_url}/datasets/${datasetId}/model-versions`)
    .then((response) => {
      return response.data;
    });
};

export const useGetDatasetModelVersions = (datasetId) => {
  return useQuery(
    ["datasetModelVersions", datasetId],
    () => getDatasetModelVersions(datasetId),
    {
      refetchOnWindowFocus: false,
    },
  );
};

export const upsertAnalytic = ({
  datasetId,
  modelVersionId,
  modelLabelClassId,
  aggregation,
  sliceBys,
  sliceStats,
  sliceProportional,
  sliceExcludeFromAverage,
  timeSlice,
  includeFirstTimePeriod,
  includeLastTimePeriod,
  timeStats,
  timeProportional,
  dataFilters,
  limitResults,
  resultsLimitedTo,
  benchmark,
}) => {
  let newAnalytic = {
    dataset_id: datasetId,
    model_version_id: modelVersionId,
    model_label_class_id: modelLabelClassId,
    config: {
      aggregation,
      slice_by: sliceBys,
      slice_stats: sliceStats,
      slice_proportional: sliceProportional,
      slice_exclude_from_average: sliceExcludeFromAverage,
      time_slice: timeSlice,
      time_stats: timeStats,
      time_proportional: timeProportional,
      filters: whereFilterListToJsonArray(dataFilters),
      include_first_time_period: includeFirstTimePeriod,
      include_last_time_period: includeLastTimePeriod,
      model_params: {
        limit_results: limitResults,
        results_limited_to: resultsLimitedTo,
      },
      benchmark,
    },
  };
  return axios
    .put(`${nuclearn_api_url}/stats/analytic`, newAnalytic)
    .then((response) => {
      return response.data;
    });
};

export const useUpsertAnalytic = () => {
  const queryClient = useQueryClient();

  return useMutation((newAnalytic) => upsertAnalytic(newAnalytic), {
    onSuccess: (analytic) => {
      queryClient.setQueryData(["analytic", analytic.id], () => analytic);
    },
    onError: (error) => {
      console.log(error.response);
    },
  });
};

const getAnalytic = (analyticId) => {
  return axios
    .get(`${nuclearn_api_url}/stats/analytic/${analyticId}`)
    .then((response) => {
      return response.data;
    });
};

export const useGetAnalytic = (analyticId) => {
  return useQuery(["analytic", analyticId], () => getAnalytic(analyticId), {
    refetchOnWindowFocus: false,
    enabled: Boolean(analyticId),
  });
};

export const calculateAnalyticResultsUrl = (
  analyticId,
  downloadAsCsv = false,
) => {
  let params = new URLSearchParams();
  params.append("download_as_csv", downloadAsCsv.toString());
  return `${nuclearn_api_url}/stats/analytic/${analyticId}/results?${params.toString()}`;
};

export const calculateRawPredictionsUrl = (analyticId) => {
  let params = new URLSearchParams();
  params.append("analytic_id", analyticId);
  params.append("include_input_data", "true");
  params.append("as_csv", "true");
  return `${nuclearn_api_url}/stats/predictions?${params.toString()}`;
};

export const getAnalyticResults = (analyticId, downloadAsCsv = false) => {
  return axios
    .get(calculateAnalyticResultsUrl(analyticId, downloadAsCsv))
    .then((response) => {
      return response.data;
    });
};

export const useGetAnalyticResults = (analyticId, enabled) => {
  return useQuery(
    ["analyticResults", analyticId],
    () => getAnalyticResults(analyticId),
    {
      retry: 1,
      refetchOnWindowFocus: false,
      enabled,
    },
  );
};

const getUnscoredDatasetStats = (datasetId, modelVersionId) => {
  return axios
    .get(
      `${nuclearn_api_url}/datasets/${datasetId}/unscored?model_version_id=${modelVersionId}`,
    )
    .then((response) => {
      return response.data;
    });
};

export const useGetUnscoredDatasetStats = (datasetId, modelVersionId) => {
  return useQuery(
    ["unscoredDatasetStats", datasetId, modelVersionId],
    () => getUnscoredDatasetStats(datasetId, modelVersionId),
    {
      refetchOnWindowFocus: false,
      enabled: (Boolean(datasetId), Boolean(modelVersionId)),
    },
  );
};

const scoreDataset = (data) => {
  let payload = {
    model_version_id: data.modelVersionId,
  };
  if (data?.limitScoredRecords) {
    payload.limit_scored_records = data?.limitScoredRecords;
  }
  if (data?.onlyNew) {
    payload.only_new = data?.onlyNew;
  }
  return axios
    .post(`${nuclearn_api_url}/datasets/${data.datasetId}/score`, payload)
    .then((response) => {
      return response.data;
    });
};

export const useScoreDataset = () => {
  const queryClient = useQueryClient();

  const mutation = useMutation(scoreDataset, {
    onSuccess: () => {
      queryClient.invalidateQueries("unscoredDatasetStats");
      queryClient.invalidateQueries("analyticResults");
    },
  });
  return mutation;
};

const getUnscoredQueue = (datasetId, model_version_id) => {
  return axios
    .get(`${nuclearn_api_url}/datasets/${datasetId}/unscored-queue`, {
      params: { model_version_id },
    })
    .then((response) => {
      return response.data;
    });
};

export const useUnscoredQueue = (datasetId, modelVersionId, enabled) => {
  const queryClient = useQueryClient();
  let [prevData, setPrevData] = useState(null);

  return useQuery(
    ["unscoredQueue", datasetId, modelVersionId],
    () =>
      getUnscoredQueue(datasetId, modelVersionId).then((data) => {
        // Invalidates query if at least one record is scored
        if (!prevData) {
          setPrevData(data);
        } else if (data.waiting < prevData.waiting) {
          queryClient.invalidateQueries([
            "isScored",
            datasetId,
            modelVersionId,
          ]);
        }
        // Update state with current data for next comparison
        setPrevData(data);

        return data;
      }),
    {
      refetchOnWindowFocus: false,
      enabled: (Boolean(datasetId), Boolean(modelVersionId), enabled),
      refetchInterval: 5000,
    },
  );
};

export const downloadDatasetFile = ({
  datasetId,
  datasetType,
  datasetName = "dataset",
  includeData = true, // New parameter
  forceSync = false, // New parameter
}) => {
  const isNotCsv = DOCUMENT_LIKE_DATASET_TYPES.includes(datasetType.name);

  if (isNotCsv) {
    // For non-CSV types, use the download-all route with new parameters
    return axios({
      url: `${nuclearn_api_url}/dataset-archive/${datasetId}/download-all`,
      method: "GET",
      responseType: "blob",
      params: {
        include_data: includeData,
        force_sync: forceSync,
      },
    })
      .then((response) => {
        if (!response.data) {
          throw new Error("No associated file found for the dataset.");
        }

        return {
          data: response.data,
          fileDetails: {
            file_type: "application/zip",
            file_name: `${datasetName}_${datasetId}_archive.zip`,
          },
        };
      })
      .then((result) => {
        const { data, fileDetails } = result;
        const url = window.URL.createObjectURL(
          new Blob([data], { type: fileDetails.file_type }),
        );
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", fileDetails.file_name);
        document.body.appendChild(link);
        link.click();

        // Clean up
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      })
      .catch((error) => {
        if (error.response && error.response.status === 501) {
          // Handle the case where the dataset is too large
          console.error(
            "Dataset too large for synchronous download. Try with force_sync=true.",
          );
          alert(
            "This dataset is too large for standard download. Please try again with 'Force synchronous download' option enabled.",
          );
        } else {
          // Handle other errors
          console.error("Error downloading dataset:", error);
          alert("Error downloading dataset. Please try again later.");
        }
      });
  } else {
    // For CSV types, keep the existing logic
    const requestConfig = {
      url: `${nuclearn_api_url}/datasets/${datasetId}/data`,
      method: "GET",
      responseType: "blob",
      params: { as_csv: true },
    };

    return axios(requestConfig)
      .then((response) => {
        if (!response.data) {
          throw new Error("No associated file found for the dataset.");
        }

        const BOM = new Uint8Array([0xef, 0xbb, 0xbf]);
        return {
          data: new Blob([BOM, response.data], {
            type: "text/csv;charset=utf-8",
          }),
          fileName: `${datasetName}_${datasetId}_data.csv`,
        };
      })
      .then((result) => {
        const { data, fileName } = result;
        const url = window.URL.createObjectURL(
          new Blob([data], { type: "text/csv" }),
        );
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", fileName);
        document.body.appendChild(link);
        link.click();

        // Clean up
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      })
      .catch((error) => {
        console.error("Error downloading dataset:", error);
        alert("Error downloading dataset. Please try again later.");
      });
  }
};
export const useDownloadDatasetFile = () => {
  return useMutation(downloadDatasetFile);
};

export const downloadAnalyticPredictionsCsv = (
  analyticId,
  includeInputData,
  callback,
) => {
  axios({
    url: `${nuclearn_api_url}/stats/predictions`,
    params: {
      analytic_id: analyticId,
      include_input_data: includeInputData,
      as_csv: true,
    },
    method: "GET",
    responseType: "blob",
  })
    .then((response) => {
      /*
       * TODO: Better file name using the request headers
       * TODO: Wrap up this login into a file download function
       */
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        `raw_predictions_${getFormattedDate()}.csv`,
      ); //or any other extension
      document.body.appendChild(link);
      link.click();
    })
    .finally(() => {
      if (callback) {
        callback();
      }
    });
};

export const getSourceDataRecord = (sourceDataRecordId) => {
  return axios
    .get(
      `${nuclearn_api_url}/datasets/record?source_data_record_id=${sourceDataRecordId}`,
    )
    .then((res) => {
      return res.data;
    });
};

export const useGetSourceDataRecord = (sourceDataRecordId) => {
  return useQuery(
    ["sourceDataRecord", sourceDataRecordId],
    () => getSourceDataRecord(sourceDataRecordId),
    {
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      enabled: Boolean(sourceDataRecordId),
    },
  );
};

export const getSourceDataRecords = (
  datasetId,

  page = 0,

  numRecsPerPg = 25,

  whereFilterList = [],
  numSearchResults,
  searchString = null,
) => {
  let params = new URLSearchParams();
  params.append("record_idx", page); // page * numrecspp gives record_idx
  params.append("num_recs_per_page", numRecsPerPg);
  let filt = whereFilterListToJsonArray(whereFilterList);
  let hasInvalidFilters = false;

  filt.forEach((filt) => {
    if (filt.filter_on.name === "" || filt.filter_on.column === "") {
      hasInvalidFilters = true;

      return;
    }
  });
  if (!hasInvalidFilters) {
    params.append("filt", JSON.stringify(filt));
  }
  if (searchString && searchString !== "null") {
    params.append("n_search_results", numSearchResults);
    params.append("search_string", searchString);
  }
  return axios
    .get(`${nuclearn_api_url}/datasets/${datasetId}/records`, {
      params,
    })
    .then((res) => {
      return res.data;
    });
};

export const getSourceDataRecordsWithEmbeddingCount = (
  datasetId,

  page = 0,

  numRecsPerPg = 25,

  whereFilterList = [],
  numSearchResults,
  searchString = "",
  sort = null,
  aggregateToSdrs = false,
  // New: use simple file_name contains filter instead of semantic search
  useFileNameFilter = false,
  // Cursor-based pagination: pass last record's ID for O(log n) deep page access
  afterCursor = null,
) => {
  let params = new URLSearchParams();
  params.append("record_idx", page); // page * numrecspp gives record_idx
  params.append("num_recs_per_page", numRecsPerPg);
  let filt = whereFilterListToJsonArray(whereFilterList);
  let hasInvalidFilters = false;

  filt.forEach((filt) => {
    if (filt.filter_on.name === "" || filt.filter_on.column === "") {
      hasInvalidFilters = true;

      return;
    }
  });
  if (!hasInvalidFilters) {
    params.append("filt", JSON.stringify(filt));
  }
  if (searchString && searchString !== "null") {
    if (useFileNameFilter) {
      // Use simple contains filter on file_name (~ prefix = LIKE/contains)
      params.append("file_name", `~${searchString}`);
    } else {
      // Use semantic/vector search
      params.append("n_search_results", numSearchResults);
      params.append("search_string", searchString);
      params.append("aggregate_to_sdrs", aggregateToSdrs);
    }
  }
  // Add sort parameter (industry-standard format: "-field" for desc, "field" for asc)
  if (sort) {
    params.append("sort", sort);
  }
  // Cursor-based pagination: when provided, bypasses OFFSET for O(log n) performance
  if (afterCursor != null) {
    params.append("after", afterCursor);
  }
  return axios
    .get(
      `${nuclearn_api_url}/datasets/${datasetId}/records-with-embedded-chunk-count`,
      {
        params,
      },
    )
    .then((res) => {
      return res.data;
    })
    .catch((err) => {
      if (err.response && err.response.status === 504) {
        NucToast({
          message:
            "Query timed out. Try narrowing your filters or reducing the page size.",
          type: "error",
        });
      }
      throw err;
    });
};

export const useAllSourceDataRecords = (
  datasetId,
  page,
  // TODO: Fix ordering

  numRecsPerPg = 10,
  whereFilterList,
) => {
  const queryClient = useQueryClient();

  // Only refetch the SDR list when an SDR itself is added/removed/changed.
  // Ignore dataset create/update/delete — those fire on the same channel but
  // don't reshape this dataset's SDR page.
  useNotificationStream(
    datasetId ? "notify:datasets:changed" : null,
    "dataset_changed",
    (data) => {
      if (
        data?.action === "sdr_status_changed" &&
        data?.dataset_id === datasetId
      ) {
        queryClient.invalidateQueries(["allSourceDataRecords", datasetId]);
      }
    },
  );

  return useQuery(
    ["allSourceDataRecords", datasetId, page, numRecsPerPg],
    () => getSourceDataRecords(datasetId, page, numRecsPerPg, whereFilterList),
    {
      keepPreviousData: true,
      refetchOnWindowFocus: false,
      enabled: Boolean(datasetId),
    },
  );
};

const getAllArtifactsByDatasetId = (datasetId) => {
  return axios
    .get(
      `${nuclearn_api_url}/datasets/${datasetId}/get-dataset-artifacts-by-id`,
    )
    .then((res) => {
      return res.data;
    });
};

export const useAllArtifactsByDatasetId = (datasetId) => {
  return useQuery(
    ["allArtifactsByDatasetId", datasetId],
    () => getAllArtifactsByDatasetId(datasetId),
    {
      keepPreviousData: true,
      refetchOnWindowFocus: false,
      enabled: datasetId !== undefined && datasetId !== null,
    },
  );
};

export const useInfiniteAllSourceDataRecords = (
  datasetId,
  page,
  // TODO: Fix ordering

  whereFilterList = [],
  numSearchResults,
  searchText,
  numRecsPerPg = 100,
) => {
  return useInfiniteQuery(
    [
      "allSourceDataRecords",
      datasetId,
      /*
       * page,
       * numRecsPerPg,
       */
      JSON.stringify(whereFilterList),
      searchText,
      // numRecsPerPg,
    ],
    ({ pageParam }) => {
      return getSourceDataRecords(
        datasetId,
        pageParam,
        numRecsPerPg,
        whereFilterList,
        numSearchResults,
        searchText,
      );
    },
    {
      retry: 2,
      getNextPageParam: (lastPage, allPages) => {
        return lastPage.length < numRecsPerPg ? undefined : allPages.length;
      },
    },
  );
};

// eslint-disable-next-line no-unused-vars
const useInfiniteAllSourceDataRecordsWithEmbeddingCount = (
  datasetId,

  whereFilterList = [],
  numSearchResults,
  searchText,
  numRecsPerPg = 100,
) => {
  return useInfiniteQuery(
    [
      "allSourceDataRecords",
      datasetId,
      JSON.stringify(whereFilterList),
      searchText,
      // numRecsPerPg,
    ],
    ({ pageParam }) => {
      return getSourceDataRecordsWithEmbeddingCount(
        datasetId,
        pageParam,
        numRecsPerPg,
        whereFilterList,
        numSearchResults,
        searchText,
      );
    },
    {
      retry: 2,
      initialPageParam: 0,
      getNextPageParam: (_lastGroup, groups) => groups.length,
      enabled: Boolean(datasetId),
      refetchOnWindowFocus: false,
    },
  );
};

export const getLabelMap = (labelName) => {
  return axios
    .get(`${nuclearn_api_url}/stats/label-map`, {
      params: {
        label_name: labelName,
      },
    })
    .then((res) => {
      return res.data;
    });
};

export const useLabelMap = (labelName) => {
  return useQuery(["labelMap", labelName], () => getLabelMap(labelName), {
    refetchOnWindowFocus: false,
  });
};

export const getStatsRecordInfo = (analyticId, statsOptions) => {
  let params = new URLSearchParams();
  params.append("analytic_id", analyticId);
  let keys = Object.keys(statsOptions);
  keys.forEach((key) => {
    if (Array.isArray(statsOptions[key])) {
      statsOptions[key].forEach((value) => {
        params.append(key, value);
      });
    } else {
      params.append(key, statsOptions[key]);
    }
  });
  return axios
    .get(`${nuclearn_api_url}/stats/record-info?${params.toString()}`)
    .then((res) => {
      return res.data;
    });
};

export const useStatsRecordInfo = (analyticId, statsOptions, disable) => {
  return useQuery(
    ["statsRecordInfo", analyticId, statsOptions],
    () => getStatsRecordInfo(analyticId, statsOptions),
    {
      retry: 2,
      enabled: !disable,
    },
  );
};

const overridePredictions = ({
  datasetId,
  sourceUid,
  modelVersionId,
  modelLabelClassOutputName,
  overrides,
}) => {
  return axios
    .patch(
      `${nuclearn_api_url}/datasets/${datasetId}/override_predictions/${encodeURIComponent(
        sourceUid,
      )}/${modelVersionId}/${encodeURIComponent(modelLabelClassOutputName)}`,
      overrides,
    )
    .then((response) => {
      return response.data;
    });
};

export const useOverridePredictions = () => {
  const queryClient = useQueryClient();

  const mutation = useMutation(overridePredictions, {
    onSuccess: () => {
      queryClient.invalidateQueries("analyticResults");
      queryClient.invalidateQueries("allPocRecords");
      queryClient.invalidateQueries("subsetDetailedPOCRecords");
      queryClient.invalidateQueries("predictionDetails");
    },
    onError: (error) => {
      console.log(error.response);
    },
  });
  return mutation;
};

export const getPredictionDetails = ({
  datasetId,
  sourceUid,
  modelVersionId,
  modelLabelClassOutputName,
}) => {
  return axios
    .get(
      `${nuclearn_api_url}/datasets/${datasetId}/prediction_details/${encodeURIComponent(
        sourceUid,
      )}/${modelVersionId}/${encodeURIComponent(modelLabelClassOutputName)}`,
    )
    .then((response) => {
      return response.data;
    });
};

export const usePredictionDetails = (
  { datasetId, sourceUid, modelVersionId, modelLabelClassOutputName },
  enabled,
) => {
  return useQuery(
    [
      "predictionDetails",
      datasetId,
      sourceUid,
      modelVersionId,
      modelLabelClassOutputName,
    ],
    () =>
      getPredictionDetails({
        datasetId,
        sourceUid,
        modelVersionId,
        modelLabelClassOutputName,
      }),
    {
      retry: 1,
      enabled,
      refetchOnWindowFocus: false,
    },
  );
};

const getDatasetTypes = () => {
  return axios.get(`${nuclearn_api_url}/datasets/types`).then((response) => {
    const data = Array.isArray(response.data) ? response.data : [];
    return data.sort((a, b) => {
      if (a.name < b.name) {
        return -1;
      }
      if (a.name > b.name) {
        return 1;
      }
      return 0;
    });
  });
};

export const useGetDatasetTypes = () => {
  return useQuery(["datasetTypes"], () => getDatasetTypes(), {
    ...CACHE_CONFIG.STATIC, // Dataset types rarely change
    retry: 2,
  });
};

// Dataset type IDs that require specific licenses
const PROJECTAI_DATASET_TYPE_ID = 3;
const ACTIVITY_DATASET_TYPE_ID = 4;

// Mapping of dataset type IDs to required license products
const DATASET_TYPE_LICENSE_MAP = {
  [PROJECTAI_DATASET_TYPE_ID]: "project-genius",
  [ACTIVITY_DATASET_TYPE_ID]: "observation-program",
};

/**
 * Hook to get dataset types filtered by license
 * Hides Activity and Project AI dataset types if the corresponding license isn't enabled
 * @param {string[] | undefined} licensedProducts - Array of licensed product names (undefined while loading)
 * @param {boolean} [isLicenseError=false] - Whether the license fetch failed (will show all types on error)
 * @returns {import('react-query').UseQueryResult<Array<{id: number, name: string}>>}
 */
export const useGetFilteredDatasetTypes = (
  licensedProducts,
  isLicenseError = false,
) => {
  const {
    data: datasetTypes,
    isLoading: datasetTypesLoading,
    ...rest
  } = useGetDatasetTypes();

  const filteredDatasetTypes = useMemo(() => {
    // Wait for dataset types to load first
    if (!datasetTypes) {
      return undefined;
    }
    // If license fetch failed, fail open - show all dataset types
    // This prevents users from being blocked if the license API is down
    if (isLicenseError) {
      return datasetTypes;
    }
    // Don't filter until licenses are loaded
    // This prevents flickering where licensed types briefly disappear
    if (licensedProducts === undefined) {
      return undefined;
    }
    return datasetTypes.filter((dsType) => {
      const requiredLicense = DATASET_TYPE_LICENSE_MAP[dsType.id];
      // If no license required for this type, include it
      if (!requiredLicense) {
        return true;
      }
      // If license is required, check if user has it
      return licensedProducts.includes(requiredLicense);
    });
  }, [datasetTypes, licensedProducts, isLicenseError]);

  // Loading if: dataset types loading, OR licenses loading (unless error), OR result still undefined
  const isLoading =
    datasetTypesLoading ||
    (licensedProducts === undefined && !isLicenseError) ||
    filteredDatasetTypes === undefined;

  return {
    data: filteredDatasetTypes,
    isLoading,
    ...rest,
  };
};

const getDatasetType = (datasetId) => {
  return axios
    .get(`${nuclearn_api_url}/datasets/${datasetId}/type-info`)
    .then((response) => {
      return response.data;
    });
};

export const useGetDatasetType = (datasetId) => {
  return useQuery(["datasetType", datasetId], () => getDatasetType(datasetId), {
    enabled: Boolean(datasetId),
  });
};

export const isDocumentDatasetType = (datasetTypeId) =>
  datasetTypeId === DOCUMENT_DATASET_TYPE;

const getSourceDataRecordChunks = (sourceDataRecordId) => {
  return axios
    .get(`${nuclearn_api_url}/datasets/${sourceDataRecordId}/chunks`)
    .then((response) => {
      return response.data;
    });
};

export const useGetSourceDataRecordChunks = (sourceDataRecordId) => {
  return useQuery(
    ["sourceDataRecordChunks", sourceDataRecordId],
    () => getSourceDataRecordChunks(sourceDataRecordId),
    {
      enabled: Boolean(sourceDataRecordId),
    },
  );
};

export const useExportCSV = () => {
  return useMutation(
    async ({ analyticId }) => {
      const analyticResults = await getAnalyticResults(analyticId, true);

      const BOM = new Uint8Array([0xef, 0xbb, 0xbf]); // Handling UTF-8 BOM for CSV files
      let blob = new Blob([BOM, analyticResults], {
        type: "text/csv;charset=utf-8",
      });
      let link = document.createElement("a");
      link.href = window.URL.createObjectURL(blob);
      link.download = `analysis_data_${getFormattedDate()}.csv`;
      link.click();
    },
    {
      onSuccess: (_, { onReadyToRunChange }) => {
        onReadyToRunChange(false);
      },
      onError: (_, { onReadyToRunChange }) => {
        onReadyToRunChange(false);
      },
    },
  );
};

export const searchDatasets = ({
  searchQuery,
  filters = [],
  folderId,
  page = null,
  pageSize = null,
  sortBy = "id",
  sortDesc = false,
  starredOnly = false,
  searchAllFolders = false,
}) => {
  let url = `${nuclearn_api_url}/datasets/search`;

  // Add query parameters for pagination and sorting
  const params = new URLSearchParams();
  if (page !== null) params.append("page", page.toString());
  if (pageSize !== null) params.append("page_size", pageSize.toString());
  if (sortBy) params.append("sort_by", sortBy);
  if (sortDesc) params.append("sort_desc", sortDesc.toString());

  if (params.toString()) {
    url += `?${params.toString()}`;
  }

  const data = {
    search_query: searchQuery,
    filters: filters,
    dataset_folder_id: folderId,
    starred_only: starredOnly,
    search_all_folders: searchAllFolders,
  };

  return axios.post(url, data).then((response) => response.data);
};

const getAllDatasetTags = () => {
  return axios.get(`${nuclearn_api_url}/datasets/tags`).then((response) => {
    return response.data.map((value) => value.tag);
  });
};

export const useGetAllDatasetTags = () => {
  return useQuery(["allDatasetTags"], getAllDatasetTags, {
    ...CACHE_CONFIG.STATIC, // Tags rarely change
  });
};

export const getDatasetFolders = () => {
  return axios.get(`${nuclearn_api_url}/dataset-folders/`).then((response) => {
    return response.data;
  });
};

export const useGetDatasetFolders = () => {
  const queryClient = useQueryClient();

  // Datasets and folders co-vary: creating/deleting a dataset often moves
  // folders too. Filter on action so SDR-status-derived broadcasts (fired
  // every ~2s per active dataset during indexing) don't force a refetch.
  useNotificationStream(
    "notify:datasets:changed",
    "dataset_changed",
    (data) => {
      const action = data?.action;
      if (
        action === "created" ||
        action === "deleted" ||
        action === "updated"
      ) {
        queryClient.invalidateQueries(["datasetFolders"]);
      }
    },
  );

  return useQuery(["datasetFolders"], getDatasetFolders);
};

const getDatasetFolder = (datasetFolderId) => {
  return axios
    .get(`${nuclearn_api_url}/dataset-folders/${datasetFolderId}`)
    .then((response) => {
      return response.data;
    });
};

export const useGetDatasetFolder = (datasetFolderId) => {
  return useQuery(
    ["datasetFolder", datasetFolderId],
    () => getDatasetFolder(datasetFolderId),
    {
      enabled: Boolean(datasetFolderId),
    },
  );
};

const deleteDatasetFolder = (datasetFolderId) => {
  return axios
    .delete(`${nuclearn_api_url}/dataset-folders/${datasetFolderId}`)
    .then((response) => {
      return response.data;
    });
};

export const useDeleteDatasetFolder = () => {
  const queryClient = useQueryClient();
  return useMutation(
    (datasetFolderId) => deleteDatasetFolder(datasetFolderId),
    {
      onSuccess: () => {
        NucToast({
          message: `Deleted successfully!`,
          type: "success",
        });
        queryClient.invalidateQueries("datasetFolders");
        queryClient.invalidateQueries("datasetList");
      },
    },
  );
};

const moveDatasetsToFolder = ({
  oldDatasetFolderId,
  newDatasetFolderId,
  datasetIds,
}) => {
  return axios
    .post(`${nuclearn_api_url}/dataset-folders/move-datasets`, {
      old_dataset_folder_id: oldDatasetFolderId,
      new_dataset_folder_id: newDatasetFolderId,
      dataset_ids: datasetIds,
    })
    .then((response) => {
      return response.data;
    });
};

export const useMoveDatasetsToFolder = () => {
  const queryClient = useQueryClient();
  return useMutation(moveDatasetsToFolder, {
    onSuccess: () => {
      queryClient.invalidateQueries("datasetFolders");
      queryClient.invalidateQueries("datasetList");
    },
  });
};

const updateDatasetFolder = (folderData) => {
  return axios
    .patch(`${nuclearn_api_url}/dataset-folders/${folderData.id}`, folderData)
    .then((response) => response.data);
};

export const useUpdateDatasetFolder = () => {
  return useMutation(updateDatasetFolder);
};

const createDatasetFolder = (folderData) => {
  return axios
    .post(`${nuclearn_api_url}/dataset-folders/`, folderData)
    .then((response) => response.data);
};

export const useCreateDatasetFolder = () => {
  const queryClient = useQueryClient();

  return useMutation(createDatasetFolder, {
    onSuccess: () => {
      queryClient.invalidateQueries("datasetFolders");
      queryClient.invalidateQueries("datasetList");
    },
  });
};

export const searchDatasetFolders = ({
  searchQuery,
  page = 1,
  pageSize = 50,
  sortBy = "create_date",
  sortDesc = true,
}) => {
  const params = new URLSearchParams();
  params.append("page", page.toString());
  params.append("page_size", pageSize.toString());
  params.append("sort_by", sortBy);
  params.append("sort_desc", sortDesc.toString());

  return axios
    .post(`${nuclearn_api_url}/dataset-folders/search?${params.toString()}`, {
      search_query: searchQuery,
    })
    .then((response) => response.data);
};

// eslint-disable-next-line no-unused-vars
const useSearchDatasetFolders = () => {
  return useMutation(searchDatasetFolders);
};

// Filter schema and facets for Amazon-style filtering
export const getFilterSchema = (datasetId) => {
  return axios
    .get(`${nuclearn_api_url}/datasets/${datasetId}/filter-schema`)
    .then((response) => response.data);
};

export const useGetFilterSchema = (datasetId) => {
  return useQuery(
    ["filterSchema", datasetId],
    () => getFilterSchema(datasetId),
    {
      ...CACHE_CONFIG.STATIC, // Filter schema rarely changes
      enabled: Boolean(datasetId),
      retry: 1,
      // Silently fail if endpoint doesn't exist - we'll fall back to local generation
      onError: () => {},
      // Return undefined on error so we use fallback
      useErrorBoundary: false,
    },
  );
};

/**
 * Fetches facet data (distinct values with counts) for specified fields.
 * Used to populate filter dropdowns/checkboxes with actual options.
 */
export const getFacets = (datasetId, fields = [], currentFilters = {}) => {
  const params = new URLSearchParams();

  // Add fields to fetch facets for
  if (fields.length > 0) {
    params.set("fields", fields.join(","));
  }

  // Add current filters for dynamic count updates
  Object.entries(currentFilters).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") {
      params.set(key, String(value));
    }
  });

  return axios
    .get(
      `${nuclearn_api_url}/datasets/${datasetId}/facets?${params.toString()}`,
    )
    .then((response) => response.data);
};

export const useGetFacets = (
  datasetId,
  fields = [],
  currentFilters = {},
  options = {},
) => {
  const filterKey = JSON.stringify(currentFilters);
  const fieldsKey = fields.join(",");

  return useQuery(
    ["facets", datasetId, fieldsKey, filterKey],
    () => getFacets(datasetId, fields, currentFilters),
    {
      ...CACHE_CONFIG.SEARCH, // Facets can change based on filters
      enabled: Boolean(datasetId) && fields.length > 0,
      keepPreviousData: true, // Show previous facets while loading new ones
      retry: 1,
      // Silently fail if endpoint doesn't exist - we'll fall back to local derivation
      onError: () => {},
      useErrorBoundary: false,
      ...options,
    },
  );
};

// Dataset Star (Favorite) functionality
export const starDataset = (datasetId) => {
  return axios
    .post(`${nuclearn_api_url}/datasets/${datasetId}/star`)
    .then((response) => response.data);
};

export const unstarDataset = (datasetId) => {
  return axios
    .delete(`${nuclearn_api_url}/datasets/${datasetId}/star`)
    .then((response) => response.data);
};

export const getDatasetStarredByUsers = (datasetId) => {
  return axios
    .get(`${nuclearn_api_url}/datasets/${datasetId}/starred-by`)
    .then((response) => response.data);
};

export const useStarDataset = () => {
  const queryClient = useQueryClient();

  return useMutation(starDataset, {
    onMutate: async (datasetId) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries(["datasetList"]);

      // Optimistically update the cache
      // The data structure is { results: [...], total_pages, total_items }
      queryClient.setQueriesData(["datasetList"], (old) => {
        if (!old?.results) return old;
        return {
          ...old,
          results: old.results.map((dataset) =>
            dataset.id === datasetId
              ? {
                  ...dataset,
                  is_starred_by_user: true,
                  star_count: (dataset.star_count || 0) + 1,
                }
              : dataset,
          ),
        };
      });

      return {};
    },
    onError: () => {
      // Revert optimistic update by refetching
      queryClient.refetchQueries(["datasetList"]);
      NucToast({
        message: "Failed to star dataset",
        type: "error",
      });
    },
    onSuccess: (data) => {
      // Refetch to sync server-side filters (e.g. starred_only)
      queryClient.refetchQueries(["datasetList"]);
      if (data.success) {
        NucToast({
          message: data.message,
          type: "success",
        });
      }
    },
  });
};

export const useUnstarDataset = () => {
  const queryClient = useQueryClient();

  return useMutation(unstarDataset, {
    onMutate: async (datasetId) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries(["datasetList"]);

      // Optimistically update the cache
      // The data structure is { results: [...], total_pages, total_items }
      queryClient.setQueriesData(["datasetList"], (old) => {
        if (!old?.results) return old;
        return {
          ...old,
          results: old.results.map((dataset) =>
            dataset.id === datasetId
              ? {
                  ...dataset,
                  is_starred_by_user: false,
                  star_count: Math.max((dataset.star_count || 0) - 1, 0),
                }
              : dataset,
          ),
        };
      });

      return {};
    },
    onError: () => {
      // Revert optimistic update by refetching
      queryClient.refetchQueries(["datasetList"]);
      NucToast({
        message: "Failed to unstar dataset",
        type: "error",
      });
    },
    onSuccess: (data) => {
      // Refetch to sync server-side filters (e.g. starred_only)
      queryClient.refetchQueries(["datasetList"]);
      if (data.success) {
        NucToast({
          message: data.message,
          type: "success",
        });
      }
    },
  });
};

export const useGetDatasetStarredByUsers = (datasetId, enabled = true) => {
  return useQuery(
    ["datasetStarredByUsers", datasetId],
    () => getDatasetStarredByUsers(datasetId),
    {
      enabled: Boolean(datasetId) && enabled,
      ...CACHE_CONFIG.USER_SPECIFIC,
    },
  );
};
